import React from "react";
import { getEventCreatedByInfosString } from "../../../extras";
import { dateTimeMask } from "../../../common/dateMask";
import { getEventCodeDescription } from "../../../utils/orders";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Timeline from "@mui/lab/Timeline";
import Typography from "@mui/material/Typography";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import { alpha, styled, useTheme } from "@mui/material/styles";

// ICONS
import HailIcon from "@mui/icons-material/Hail";
import InfoIcon from "@mui/icons-material/Info";
import TimerIcon from "@mui/icons-material/Timer";
import PushPinIcon from "@mui/icons-material/PushPin";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import BackspaceIcon from "@mui/icons-material/Backspace";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import TimeToLeaveIcon from "@mui/icons-material/TimeToLeave";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BookmarkRemoveIcon from "@mui/icons-material/BookmarkRemove";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import BreakfastDiningIcon from "@mui/icons-material/BreakfastDining";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";

// Styled components
const EventTimeline = styled(Timeline)(({ theme }) => ({
  padding: 0,
  margin: 0,
  [theme.breakpoints.down("md")]: {
    padding: "6px 0",
  },
  "@media print": {
    color: "#000 !important",
  },
}));

const EventDetail = styled(Button)(({ theme }) => ({
  borderRadius: 4,
  textTransform: "none",
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  color: theme.palette.primary.dark,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  fontSize: "0.8rem",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  "@media print": {
    backgroundColor: "transparent !important",
    border: "1px solid #000 !important",
    color: "#000 !important",
  },
}));

const EventDot = styled(TimelineDot)(({ theme, eventType }) => {
  const colorMap = {
    PLC: { color: theme.palette.success.dark, bg: alpha(theme.palette.success.light, 0.2) },
    CFM: { color: theme.palette.success.main, bg: alpha(theme.palette.success.light, 0.2) },
    RTP: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.light, 0.2) },
    DSP: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.light, 0.2) },
    CON: { color: theme.palette.success.main, bg: alpha(theme.palette.success.light, 0.2) },
    CAR: { color: theme.palette.warning.main, bg: alpha(theme.palette.warning.light, 0.2) },
    CAN: { color: theme.palette.error.main, bg: alpha(theme.palette.error.light, 0.2) },
    CCR: { color: theme.palette.error.main, bg: alpha(theme.palette.error.light, 0.2) },
    CCA: { color: theme.palette.error.main, bg: alpha(theme.palette.error.light, 0.2) },
    PAA: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.light, 0.2) },
    OPE: { color: theme.palette.warning.dark, bg: alpha(theme.palette.warning.light, 0.2) },
    OBA: { color: theme.palette.success.dark, bg: alpha(theme.palette.success.light, 0.2) },
    OBR: { color: theme.palette.error.main, bg: alpha(theme.palette.error.light, 0.2) },
  };

  const eventColor = colorMap[eventType] || {
    color: theme.palette.grey[600],
    bg: alpha(theme.palette.grey[300], 0.3),
  };

  return {
    boxShadow: `0 0 0 3px ${alpha(eventColor.color, 0.1)}`,
    backgroundColor: eventColor.bg,
    color: eventColor.color,
    borderColor: alpha(eventColor.color, 0.3),
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "@media print": {
      boxShadow: "0 0 0 2px #000 !important",
      backgroundColor: "#fff !important",
      color: "#000 !important",
      borderColor: "#000 !important",
    },
  };
});

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  border: `1px dashed ${theme.palette.grey[400]}`,
  "@media print": {
    backgroundColor: "#fff !important",
    border: "1px dashed #000 !important",
    color: "#000 !important",
  },
}));

export default function EventsInfos({
  events = [],
  handleEventBodyModal = null,
}) {
  const theme = useTheme();
  const hasEvents = events && events.length > 0;

  const getEventIcon = (key) => {
    switch (key) {
      case "PLC":
        return <PushPinIcon />;
      case "CFM":
        return <ThumbUpIcon />;
      case "PKD":
        return <BreakfastDiningIcon />;
      case "PREDSP":
        return <AdsClickIcon />;
      case "RTP":
        return <HailIcon />;
      case "DSP":
        return <DeliveryDiningIcon />;
      case "CON":
        return <DoneAllIcon />;
      case "CAR":
        return <TimelapseIcon />;
      case "CAN":
        return <BackspaceIcon />;
      case "CCR":
        return <PersonOffIcon />;
      case "CCA":
        return <BackspaceIcon />;
      case "PAA":
        return <TimeToLeaveIcon />;
      case "OPE":
        return <ProductionQuantityLimitsIcon />;
      case "OBA":
        return <LoyaltyIcon />;
      case "OBR":
        return <BookmarkRemoveIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getEventSeverity = (code) => {
    const highSeverity = ["CAN", "CCR", "CCA", "OBR"];
    const mediumSeverity = ["CAR", "OPE"];

    if (highSeverity.includes(code)) return "high";
    if (mediumSeverity.includes(code)) return "medium";
    return "low";
  };

  return (
    <Box>
      {hasEvents && (
        <Chip
          icon={<TimerIcon />}
          label={`${events.length} eventos registrados`}
          size="small"
          sx={{
            mb: 2,
            fontSize: "0.75rem",
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.dark,
            "@media print": {
              backgroundColor: "#fff !important",
              color: "#000 !important",
              border: "1px solid #000 !important",
            },
          }}
        />
      )}

      {!hasEvents ? (
        <EmptyState>
          <ErrorOutlineIcon
            sx={{
              fontSize: 40,
              color: theme.palette.grey[400],
              mb: 1,
              "@media print": { color: "#000 !important" },
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{
              "@media print": {
                color: "#000 !important",
              },
            }}
          >
            Nenhum evento foi registrado para este pedido.
          </Typography>
        </EmptyState>
      ) : (
        <EventTimeline>
          {events.map((event, index) => {
            const { code, created_by, created_by_info, createdat, body } =
              event;
            const severity = getEventSeverity(code);
            const isLastItem = index === events.length - 1;

            let dotVariant = "outlined";
            let connectorColor = theme.palette.grey[300];

            if (severity === "high") {
              dotVariant = "filled";
              connectorColor = alpha(theme.palette.error.light, 0.3);
            } else if (severity === "medium") {
              connectorColor = alpha(theme.palette.warning.light, 0.3);
            }

            return (
              <TimelineItem key={`event-${index}`}>
                <TimelineOppositeContent
                  sx={{
                    maxWidth: "30%",
                    flex: 0.3,
                    "@media print": { color: "#000 !important" },
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      fontWeight: 500,
                      mb: 0.5,
                      "@media print": {
                        color: "#000 !important",
                        fontWeight: "600 !important",
                      },
                    }}
                  >
                    {dateTimeMask(createdat)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: "0.7rem",
                      color: theme.palette.text.disabled,
                      "@media print": {
                        color: "#000 !important",
                      },
                    }}
                  >
                    {created_by
                      ? getEventCreatedByInfosString(
                          created_by_info ?? null
                        )
                      : "Sistema"}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <EventDot variant={dotVariant} eventType={code}>
                    {getEventIcon(code)}
                  </EventDot>
                  {!isLastItem && (
                    <TimelineConnector
                      sx={{
                        bgcolor: connectorColor,
                        "@media print": { bgcolor: "#000 !important" },
                      }}
                    />
                  )}
                </TimelineSeparator>

                <TimelineContent
                  sx={{
                    py: 1,
                    px: 2,
                    "@media print": { color: "#000 !important" },
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        "@media print": {
                          color: "#000 !important",
                          fontWeight: "700 !important",
                        },
                      }}
                    >
                      {getEventCodeDescription(code)}
                    </Typography>

                    {body && handleEventBodyModal && (
                      <Box mt={1}>
                        <EventDetail
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() =>
                            handleEventBodyModal(
                              body,
                              code,
                              created_by_info
                            )
                          }
                          size="small"
                        >
                          Ver Detalhes
                        </EventDetail>
                      </Box>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </EventTimeline>
      )}
    </Box>
  );
}
